/**
 * Twin connections resource — typed wrappers over `/v1/twin/connections`
 * plus the server↔UI mappers (B5.1).
 *
 * The server models a connection as endpoint columns
 * (from/to entity + port + material_class) and carries the UI edge
 * decoration ({ label, style }) in the opaque `data` column.
 */

import { api } from "@/lib/api-client";
import type { EnergyClass, MaterialClass, PlantEdge } from "@foundry/foundry-plant";
import { qs } from "./qs";
import type { TwinConnection, TwinConnectionList } from "./types";

export interface TwinConnectionCreate {
  id: string;
  from_entity: string;
  from_port: string;
  to_entity: string;
  to_port: string;
  material_class: string;
  /** Energy flow class; empty/omitted for material edges (P2.1). */
  energy_class?: string;
  data: unknown;
}

export const twinConnections = {
  list: (params?: { org_id?: string; limit?: number; offset?: number }) =>
    api.get<TwinConnectionList>(`/v1/twin/connections${qs(params)}`),
  create: (body: TwinConnectionCreate) =>
    api.post<TwinConnection>("/v1/twin/connections", body),
  remove: (id: string) => api.delete<void>(`/v1/twin/connections/${id}`),
};

interface PlantEdgeBlob {
  label?: string;
  style?: "solid" | "dashed";
}

function asBlob(data: unknown): PlantEdgeBlob {
  return data && typeof data === "object" ? (data as PlantEdgeBlob) : {};
}

/** Server TwinConnection → UI PlantEdge. */
export function toPlantEdge(c: TwinConnection): PlantEdge {
  const blob = asBlob(c.data);
  return {
    id: c.id,
    source: c.from_entity,
    target: c.to_entity,
    sourceHandle: c.from_port,
    targetHandle: c.to_port,
    label: blob.label ?? "",
    // A connection is material XOR energy: prefer energy_class when set.
    materialClass: c.energy_class ? undefined : (c.material_class as MaterialClass),
    energyClass: c.energy_class ? (c.energy_class as EnergyClass) : undefined,
    style: blob.style,
  };
}

/** UI PlantEdge → server create body. */
export function plantEdgeToCreate(edge: PlantEdge): TwinConnectionCreate {
  return {
    id: edge.id,
    from_entity: edge.source,
    from_port: edge.sourceHandle,
    to_entity: edge.target,
    to_port: edge.targetHandle,
    material_class: edge.materialClass ?? "",
    energy_class: edge.energyClass,
    data: { label: edge.label, style: edge.style ?? "solid" },
  };
}
